import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { img, inr } from "@/lib/api";
import { Cta } from "@/components/Cta";

export const Totals = ({ quote, compact }) => (
  <dl className={`space-y-2.5 ${compact ? "text-[15px]" : "text-[16px]"}`}>
    <div className="flex justify-between"><dt className="text-plum/70">Subtotal</dt><dd className="font-bold" data-testid="totals-subtotal">{inr(quote.subtotal)}</dd></div>
    {quote.savings > 0 && <div className="flex justify-between text-emerald"><dt>You save</dt><dd className="font-bold">− {inr(quote.savings)}</dd></div>}
    <div className="flex justify-between"><dt className="text-plum/70">Shipping</dt><dd className="font-bold" data-testid="totals-shipping">{quote.shipping ? inr(quote.shipping) : "Free"}</dd></div>
    <div className="flex justify-between items-baseline pt-3 border-t border-plum/10"><dt className="font-bold">Total</dt><dd className="font-display text-[30px] font-semibold" data-testid="totals-total">{inr(quote.total)}</dd></div>
  </dl>
);

const Line = ({ c, i, p }) => {
  const { updateQty, removeItem } = useStore();
  if (!p) return null;
  return (
    <li className="flex gap-4 py-5 border-b border-plum/10" data-testid={`bag-line-${p.slug}`}>
      <img src={img(p.images[0])} alt={p.name} className="w-[88px] h-[110px] object-cover rounded-[14px] flex-none" />
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex justify-between gap-2">
          <Link to={`/product/${p.slug}`} className="font-display text-[21px] leading-tight font-semibold">{p.name}</Link>
          <button onClick={() => removeItem(i)} aria-label="Remove" data-testid={`bag-remove-${p.slug}`} className="text-plum/50 hover:text-ruby h-8 w-8 grid place-items-center rounded-lg"><Trash2 className="w-4 h-4" /></button>
        </div>
        {c.variant && <p className="text-[14px] text-plum/70 mt-1">{p.variant_name}: {c.variant}</p>}
        <div className="mt-auto flex items-center justify-between pt-3">
          <div className="flex items-center rounded-[12px] border border-plum/15">
            <button onClick={() => updateQty(i, c.quantity - 1)} aria-label="Decrease" data-testid={`bag-qty-minus-${p.slug}`} className="w-10 h-10 grid place-items-center"><Minus className="w-4 h-4" /></button>
            <span className="w-8 text-center font-bold" data-testid={`bag-qty-${p.slug}`}>{c.quantity}</span>
            <button onClick={() => updateQty(i, c.quantity + 1)} aria-label="Increase" data-testid={`bag-qty-plus-${p.slug}`} className="w-10 h-10 grid place-items-center"><Plus className="w-4 h-4" /></button>
          </div>
          <span className="font-extrabold text-[17px]">{inr(p.effective_price * c.quantity)}</span>
        </div>
      </div>
    </li>
  );
};

export const BagDrawer = () => {
  const { bagOpen, setBagOpen, cart, products, quote, quoteError } = useStore();
  const nav = useNavigate();
  const left = quote ? quote.free_shipping_threshold - quote.subtotal : 0;
  return (
    <Sheet open={bagOpen} onOpenChange={setBagOpen}>
      <SheetContent side="right" className="w-full sm:max-w-[460px] bg-pearl p-0 sm:rounded-l-[22px] border-0 flex flex-col" data-testid="bag-drawer">
        <div className="px-6 pt-6 pb-4 border-b border-plum/10">
          <SheetTitle className="font-display text-[32px] font-semibold text-plum">Your bag</SheetTitle>
          <SheetDescription className="text-[14px] text-plum/70">{cart.length ? `${cart.reduce((s, c) => s + c.quantity, 0)} piece(s)` : "Nothing here yet"}</SheetDescription>
        </div>
        {cart.length === 0 ? (
          <div className="flex-1 grid place-items-center px-8 text-center">
            <div>
              <ShoppingBag className="w-10 h-10 mx-auto text-berry" strokeWidth={1.4} />
              <p className="font-display text-[26px] mt-4">Your bag is waiting</p>
              <p className="text-plum/70 mt-2 mb-8">Three pieces. One of them is yours.</p>
              <Cta label="Explore the edit" to="/#edit" testId="bag-empty-explore" />
            </div>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto px-6">
              {cart.map((c, i) => <Line key={`${c.product_id}-${c.variant}`} c={c} i={i} p={products.find((p) => p.id === c.product_id)} />)}
            </ul>
            <div className="px-6 py-5 border-t border-plum/10 bg-ivory space-y-4">
              {quoteError && <p className="text-[14px] font-semibold text-ruby" data-testid="bag-error">{quoteError}</p>}
              {quote && left > 0 && (
                <div>
                  <p className="text-[14px] font-semibold mb-2">Add {inr(left)} more for free shipping</p>
                  <div className="h-1.5 rounded-full bg-plum/10 overflow-hidden"><div className="h-full bg-berry" style={{ width: `${Math.min(100, (quote.subtotal / quote.free_shipping_threshold) * 100)}%` }} /></div>
                </div>
              )}
              {quote && <Totals quote={quote} compact />}
              <Cta full size="lg" variant="berry" label="Checkout securely" testId="bag-checkout-button" disabled={!quote} onClick={() => { setBagOpen(false); nav("/checkout"); }} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const AddedNotice = () => {
  const { added, setAdded, setBagOpen } = useStore();
  useEffect(() => {
    if (!added) return;
    const t = setTimeout(() => setAdded(null), 5500);
    return () => clearTimeout(t);
  }, [added, setAdded]);
  return (
    <AnimatePresence>
      {added && (
        <motion.div
          key={added.at}
          initial={{ opacity: 0, y: -16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          data-testid="added-to-bag-notice"
          className="fixed z-50 left-3 right-3 bottom-3 sm:left-auto sm:bottom-auto sm:top-[120px] sm:right-6 sm:w-[400px] rounded-[16px] bg-ivory border border-plum/10 shadow-[0_24px_60px_-20px_rgba(48,32,45,.35)] p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="flex items-center gap-2 text-[14px] font-extrabold text-emerald"><span className="w-6 h-6 rounded-full bg-emerald text-white grid place-items-center"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>Added to your bag</p>
            <button onClick={() => setAdded(null)} aria-label="Close" className="w-8 h-8 grid place-items-center rounded-lg hover:bg-plum/5"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-3 items-center">
            <img src={img(added.product.images[0])} alt="" className="w-16 h-16 rounded-[12px] object-cover" />
            <div className="min-w-0">
              <p className="font-display text-[20px] font-semibold leading-tight truncate">{added.product.name}</p>
              <p className="text-[14px] text-plum/70">{added.variant ? `${added.variant} · ` : ""}{inr(added.product.effective_price)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <button onClick={() => { setAdded(null); setBagOpen(true); }} data-testid="added-view-bag" className="h-12 rounded-[14px] bg-plum text-pearl text-[13px] font-extrabold uppercase tracking-[0.14em] hover:bg-berry transition-colors">View bag</button>
            <button onClick={() => setAdded(null)} data-testid="added-continue" className="h-12 rounded-[14px] border border-plum/20 text-[13px] font-extrabold uppercase tracking-[0.14em] hover:border-plum transition-colors">Continue</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
